import { t } from './i18n.ts';
import { h } from './ui/dom.ts';

/**
 * After a deploy, players with the game open keep the old version until they choose to reload: a small
 * notice offers it, nothing reloads on its own (a reload mid-wave would cost them the fight). It checks
 * /version.json every few minutes and whenever the tab comes back, and also when a lazily loaded file of
 * the old version has gone missing.
 */
const EVERY_MS = 3 * 60_000;
let shown = false;
let dismissed = '';

const notice = (build: string): void => {
  if (shown || dismissed === build) return;
  shown = true;
  const el = h(
    'div.update-notice',
    { role: 'status', 'data-test': 'update-notice' },
    h('span', {}, t('update.available')),
    h('button.btn.small', { onclick: () => location.reload() }, t('update.reload')),
    h('button.link', {
      onclick: () => {
        dismissed = build;
        shown = false;
        el.remove();
      },
    }, t('update.later')),
  );
  document.body.append(el);
};

/** The build the server is serving now, when it isn't this page's (null when it is, or unknown: offline, dev). */
const newerBuild = async (): Promise<string | null> => {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store', signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const { build } = (await res.json()) as { build?: string };
    return build && build !== __BUILD__ ? build : null;
  } catch {
    return null;
  }
};

const check = async (): Promise<void> => {
  const build = await newerBuild();
  if (build) notice(build);
};

/**
 * Before joining a live room: a page from before a deploy runs the old simulation against the room's new one
 * and desyncs every few seconds (stutters, or gets stuck on a snapshot it doesn't know). Not in a fight yet,
 * so reloading costs nothing: true when it reloads (the caller stops there).
 */
export const reloadIfStale = async (midFight = false): Promise<boolean> => {
  if (import.meta.env.DEV || !(await newerBuild())) return false;
  if (midFight) {
    // A deploy reached this campaign's room while we were in it (docs/DEPLOY.md): the room went on with the new
    // code, so the page must be new too. The bastion takes us back in after the reload (`rejoinPending`).
    try {
      sessionStorage.setItem(REJOIN_KEY, String(Date.now()));
    } catch {
      /* no storage: the rejoin card is still there to tap */
    }
    document.body.append(h('div.update-notice', { role: 'status' }, h('span', {}, t('update.updating'))));
  }
  location.reload();
  return true;
};

const REJOIN_KEY = 'somnhold.rejoin';
/** How long after such a reload the bastion still takes us back into our room by itself. */
const REJOIN_MS = 3 * 60_000;

/** Reloaded for a new version in the middle of a live room, a moment ago: go back in without a tap. */
export const rejoinPending = (): boolean => {
  try {
    const at = Number(sessionStorage.getItem(REJOIN_KEY) ?? 0);
    if (at && Date.now() - at < REJOIN_MS) return true;
    sessionStorage.removeItem(REJOIN_KEY);
  } catch {
    /* no storage */
  }
  return false;
};

/** Back in (or the room is gone): forget it. */
export const rejoinDone = (): void => {
  try {
    sessionStorage.removeItem(REJOIN_KEY);
  } catch {
    /* no storage */
  }
};

export const watchForUpdates = (): void => {
  if (import.meta.env.DEV) return;
  window.setInterval(() => void check(), EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  // A chunk of the old build is gone after a deploy: offer the reload instead of breaking.
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault();
    notice('missing-chunk');
  });
};
